import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CalendarClock, CheckCircle2, Clock, Phone, Mail } from 'lucide-react';
import { publicEspaceClientApi } from '../../lib/api';
import { resolveLogoUrl } from '../../lib/resolveLogoUrl';

type EtatEspace = 'fait' | 'planifie' | 'en_attente' | 'a_planifier' | 'en_cours';
interface VisiteEspace { rdv_id: string; date: string; heure: string; etat: EtatEspace; libelle: string; avant: boolean; apres: boolean; nb_ensemble: number }
interface PassageEspace {
    numero: number | null; periode: string; libelle: string; date: string | null; heure: string | null; etat: EtatEspace;
    rdv_id?: string | null; avant?: boolean; apres?: boolean;
    progression?: { fait: number; prevu: number; pourcentage: number } | null;
    visites?: VisiteEspace[]; reste_a_planifier?: number;
}
interface EspaceInfo {
    company_name: string; logo_url: string | null; brand_color: string; contact_phone: string; contact_email: string;
    label: string; prenom: string;
    devis: { total_ttc: number; signe: boolean } | null;
    reglement: { libelle: string } | null;
    prochain_rdv: { date: string; heure: string; libelle: string; confirme: boolean } | null;
    passages: PassageEspace[];
    supplementaires?: VisiteEspace[];
    peut_planifier: boolean;
}

const fmtDate = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(`${d}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); } catch { return d; }
};
const fmtEuro = (n: number) => n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const LIBELLE_ETAT: Record<EtatEspace, string> = { fait: 'Réalisé', planifie: 'Planifié', en_attente: 'Demande en cours de validation', a_planifier: 'À planifier', en_cours: 'En cours' };

/** Photos d'une visite terminée : avant/après (particuliers) et photos d'ensemble (entreprises). */
function PhotosDeVisite({ visite, token, couleur }: { visite: VisiteEspace; token: string; couleur: string }) {
    const nbEnsemble = Math.min(6, visite.nb_ensemble || 0);
    if (visite.etat !== 'fait' || !(visite.avant || visite.apres || nbEnsemble > 0)) return null;
    const url = (q: string) => `/api/espace-client/${token}/photo/${visite.rdv_id}/${q}`;
    const vues: { q: string; legende: string }[] = [];
    if (visite.avant) vues.push({ q: 'avant', legende: 'Avant' });
    if (visite.apres) vues.push({ q: 'apres', legende: 'Après' });
    for (let k = 0; k < nbEnsemble; k++) vues.push({ q: 'e' + k, legende: "Vue d'ensemble " + (k + 1) });
    return (
        <details className="mt-1.5">
            <summary className="text-xs font-bold cursor-pointer" style={{ color: couleur }}>Voir les photos ({vues.length})</summary>
            <div className="grid grid-cols-2 gap-2 mt-2">
                {vues.map((v) => (
                    <figure key={v.q}>
                        <img src={url(v.q)} alt={v.legende} loading="lazy" className="w-full rounded-lg border border-gray-200 object-cover aspect-[4/3]" />
                        <figcaption className="text-[11px] text-gray-500 text-center mt-0.5">{v.legende}</figcaption>
                    </figure>
                ))}
            </div>
        </details>
    );
}

/** Visites d'un passage (un passage peut se faire en plusieurs visites, sur plusieurs jours). */
function VisitesDuPassage({ passage, token, couleur }: { passage: PassageEspace; token: string; couleur: string }) {
    const visites = passage.visites || [];
    if (visites.length === 0) return null;
    return (
        <ul className="mt-2 space-y-1.5">
            {visites.map((v, k) => (
                <li key={v.rdv_id} className="text-xs text-gray-600 border-l-2 pl-2.5" style={{ borderColor: v.etat === 'fait' ? '#10b981' : couleur }}>
                    <span className="font-bold">{visites.length > 1 ? `Visite ${k + 1}` : 'Rendez-vous'}</span> · {fmtDate(v.date)} à {v.heure} · <span className="text-gray-500">{LIBELLE_ETAT[v.etat]}</span>
                    <PhotosDeVisite visite={v} token={token} couleur={couleur} />
                </li>
            ))}
        </ul>
    );
}

/**
 * Espace client (2026-10-18, audit concurrents) : une page sans mot de passe, derrière le lien permanent du
 * dossier (le même que la réservation). Elle répond aux questions que le client poserait au téléphone :
 * quand est mon prochain passage, qu'est-ce qui est déjà fait, comment je paie. Lecture seule ; la prise de
 * rendez-vous passe par la page de réservation existante.
 */
export const EspaceClientPage: React.FC = () => {
    // Même fond clair que les autres pages publiques.
    React.useEffect(() => {
        const root = document.documentElement;
        const themeAvant = root.getAttribute('data-theme');
        const eraitSombre = root.classList.contains('dark');
        root.setAttribute('data-theme', 'light');
        root.classList.remove('dark');
        return () => {
            if (themeAvant === null) root.removeAttribute('data-theme'); else root.setAttribute('data-theme', themeAvant);
            if (eraitSombre) root.classList.add('dark');
        };
    }, []);

    const { token } = useParams<{ token: string }>();
    const [info, setInfo] = React.useState<EspaceInfo | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [erreur, setErreur] = React.useState('');

    React.useEffect(() => {
        if (!token) return;
        publicEspaceClientApi.get(token).then((r: EspaceInfo) => setInfo(r)).catch((e: any) => setErreur(e?.message || 'Lien invalide ou expiré.')).finally(() => setLoading(false));
    }, [token]);

    if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="animate-spin text-gray-400" /></div>;
    if (erreur || !info) return <div className="min-h-screen flex items-center justify-center bg-white p-6 text-center text-gray-600">{erreur || 'Lien invalide ou expiré.'}</div>;

    const couleur = info.brand_color || '#987b55';
    const faits = info.passages.filter((p) => p.etat === 'fait').length;
    const logo = info.logo_url ? resolveLogoUrl(info.logo_url) : null;

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 text-gray-900">
            <div className="max-w-xl mx-auto space-y-4">
                <div className="text-center space-y-2">
                    {logo && <img src={logo} alt={info.company_name} className="h-12 mx-auto object-contain" />}
                    <h1 className="text-xl font-black">{info.prenom ? `Bonjour ${info.prenom}` : 'Votre espace client'}</h1>
                    <p className="text-sm text-gray-500">{info.company_name} · {info.label}</p>
                </div>

                {info.prochain_rdv ? (
                    <div className="rounded-2xl bg-white border border-gray-200 p-5 shadow-sm">
                        <p className="text-[11px] font-black uppercase tracking-widest text-gray-400 mb-1">Prochain rendez-vous</p>
                        <p className="text-lg font-black flex items-center gap-2" style={{ color: couleur }}><CalendarClock size={20} /> {fmtDate(info.prochain_rdv.date)} à {info.prochain_rdv.heure}</p>
                        <p className="text-sm text-gray-600 mt-1">{info.prochain_rdv.libelle}</p>
                        {!info.prochain_rdv.confirme && <p className="text-xs text-amber-600 mt-2">En attente de confirmation par {info.company_name}.</p>}
                    </div>
                ) : (
                    <div className="rounded-2xl bg-white border border-gray-200 p-5 shadow-sm text-sm text-gray-600">
                        Aucun rendez-vous à venir pour le moment.
                    </div>
                )}

                {info.peut_planifier && (
                    <a href={`/prestation-reservation/${token}`} className="block text-center rounded-xl py-3 font-bold text-white shadow-sm" style={{ backgroundColor: couleur }}>
                        Choisir une date pour un passage
                    </a>
                )}

                <a href={`/prestation-reservation/${token}?mode=hors-contrat`} className="block text-center rounded-xl py-2.5 text-sm font-bold border-2 bg-white" style={{ borderColor: couleur, color: couleur }}>
                    Planifier un passage supplémentaire (hors contrat)
                </a>

                {info.passages.length > 0 && (
                    <div className="rounded-2xl bg-white border border-gray-200 p-5 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-[11px] font-black uppercase tracking-widest text-gray-400">Vos passages</p>
                            <p className="text-xs font-bold text-gray-500">{faits} sur {info.passages.length} réalisé{faits > 1 ? 's' : ''}</p>
                        </div>
                        <ul className="divide-y divide-gray-100">
                            {info.passages.map((p, i) => (
                                <li key={i} className="py-3 flex items-start gap-3">
                                    {p.etat === 'fait' ? <CheckCircle2 size={18} className="text-emerald-500 mt-0.5 shrink-0" /> : <Clock size={18} className={(p.etat === 'a_planifier' ? 'text-gray-300' : 'text-amber-500') + ' mt-0.5 shrink-0'} />}
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-bold">{p.numero != null ? `Passage ${p.numero}` : p.libelle}{p.periode ? ` · ${p.periode}` : ''}</p>
                                        {p.numero != null && <p className="text-xs text-gray-500 truncate">{p.libelle}</p>}
                                        <p className="text-xs text-gray-500">{LIBELLE_ETAT[p.etat]}</p>
                                        {p.progression && p.progression.prevu > 0 && (
                                            <div className="mt-1.5">
                                                <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden" role="progressbar" aria-valuenow={p.progression.pourcentage} aria-valuemin={0} aria-valuemax={100} aria-label="Avancement du passage">
                                                    <div className="h-full rounded-full" style={{ width: `${p.progression.pourcentage}%`, backgroundColor: couleur }} />
                                                </div>
                                                <p className="text-[11px] text-gray-500 mt-0.5">{p.progression.fait} sur {p.progression.prevu} réalisés</p>
                                            </div>
                                        )}
                                        <VisitesDuPassage passage={p} token={token || ''} couleur={couleur} />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {(info.supplementaires || []).length > 0 && (
                    <div className="rounded-2xl bg-white border border-gray-200 p-5 shadow-sm">
                        <p className="text-[11px] font-black uppercase tracking-widest text-gray-400 mb-2">Passages supplémentaires</p>
                        <ul className="divide-y divide-gray-100">
                            {(info.supplementaires || []).map((v) => (
                                <li key={v.rdv_id} className="py-2.5">
                                    <p className="text-sm font-bold">{v.libelle}</p>
                                    <p className="text-xs text-gray-500">{LIBELLE_ETAT[v.etat]} — {fmtDate(v.date)} à {v.heure}</p>
                                    <PhotosDeVisite visite={v} token={token || ''} couleur={couleur} />
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {(info.devis || info.reglement) && (
                    <div className="rounded-2xl bg-white border border-gray-200 p-5 shadow-sm space-y-1.5 text-sm">
                        <p className="text-[11px] font-black uppercase tracking-widest text-gray-400">Devis et paiement</p>
                        {info.devis && <p>Devis {info.devis.signe ? 'signé' : 'en attente de signature'} : <b>{fmtEuro(info.devis.total_ttc)}</b> TTC</p>}
                        {info.reglement && <p>Mode de paiement choisi : <b>{info.reglement.libelle}</b></p>}
                    </div>
                )}

                {(info.contact_phone || info.contact_email) && (
                    <div className="text-center text-sm text-gray-500 space-y-1">
                        <p>Une question ? Contactez {info.company_name}</p>
                        {info.contact_phone && <p className="flex items-center justify-center gap-1.5"><Phone size={14} /> <a href={`tel:${info.contact_phone}`} className="font-bold">{info.contact_phone}</a></p>}
                        {info.contact_email && <p className="flex items-center justify-center gap-1.5"><Mail size={14} /> <a href={`mailto:${info.contact_email}`} className="font-bold">{info.contact_email}</a></p>}
                    </div>
                )}
            </div>
        </div>
    );
};
